import * as R from 'ramda';
import { ActionType } from '../../core/redux/actionType';
import { ApiReducer, makeApiAction, makeApiReducer, makeRsaaTypes } from '../../core/api/rsaa';
import { addError, addLoading, noLoadingNoError, getLoading } from '../../core/api/module';
import { Endpoints } from '../../core/api/endpoints';
import { ReportDeliveryRequest, ReportDeliveryResponse } from '../models';
import { toMap } from '../../language/arrays';


const KEY_ACTION = ActionType.DeliverReportsToRecipientRequest;
const ACTIONS    = [
  ActionType.DeliverReportsToRecipientRequest,
  ActionType.DeliverReportsToRecipientResponse,
  ActionType.DeliverReportsToRecipientError,
];


type Payload = ReportDeliveryResponse;


type Meta = {
  deliveryRequest: ReportDeliveryRequest[];
  recipientEmail: string;
  recipientFirstName: string;
  recipientLastName: string;
  languageId: number;
};


/**
 * Dispatch this to deliver reports to a recipient
 */
export const deliverReportsToRecipientAction = (
  deliveryRequest: ReportDeliveryRequest[],
  recipientEmail: string,
  recipientFirstName: string,
  recipientLastName: string,
  languageId: number,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.DeliverReportsToRecipient,
    headers:  { 'content-type': 'application/json' },
    body:     JSON.stringify({
      reports: deliveryRequest,
      recipientEmail: recipientEmail,
      recipientFirstName: recipientFirstName,
      recipientLastName: recipientLastName,
      languageId: languageId,
    }),
    types:    makeRsaaTypes<Payload, Meta>(ACTIONS, { 
      deliveryRequest, 
      recipientEmail, 
      recipientFirstName, 
      recipientLastName, 
      languageId,
    }),
    bailout:  state => !!getLoading(state, KEY_ACTION),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const metaTag = `${action.meta.deliveryRequest}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  const allReports = toMap(
    state.reportRecipientReports || [],
    r => `${r.reportSetId}-${r.participantId}-${r.reportId}`,
  );

  const delivered = toMap(
    payload?.reports || [],
    r => `${r.reportSetId}-${r.participantId}-${r.reportId}`,
  ); 

  const merged = R.mergeRight(allReports, delivered);

  return R.assoc('reportRecipientReports', Object.values(merged), successState);
};

export const deliverReportsToRecipientReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);